import axios, { AxiosInstance, AxiosResponse } from "axios";
import { AuthResponse, RegisterRequest, LoginRequest } from "../types/auth";
import { API_BASE_URLS, API_ENDPOINTS, HTTP_STATUS } from "../constants/api";
import { Lead, SyncStatus } from "../types/leads";

const API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? API_BASE_URLS.PRODUCTION
    : API_BASE_URLS.STAGING;

class ApiService {
  private api: AxiosInstance;
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<string> | null = null;
  private isRedirecting: boolean = false;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("accessToken");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error?.response?.status;
        const originalRequest = error?.config as any;
        const requestUrl = String(originalRequest?.url || "");

        // Special handling for subscription-status endpoint - don't retry, just reject
        if (
          status === HTTP_STATUS.UNAUTHORIZED &&
          requestUrl.includes("/subscription-status")
        ) {
          // Clear all storage and redirect to sign in
          this.clearAllStorageAndRedirect();
          return Promise.reject(error);
        }

        if (
          status === HTTP_STATUS.UNAUTHORIZED &&
          originalRequest &&
          !originalRequest._retry
        ) {
          // Don't try to refresh for the refresh endpoint itself
          if (requestUrl.includes(API_ENDPOINTS.AUTH.REFRESH)) {
            this.clearTokensAndRedirect();
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          try {
            const newAccess = await this.getValidAccessToken();
            if (!newAccess) {
              this.clearTokensAndRedirect();
              return Promise.reject(error);
            }
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newAccess}`;
            return this.api(originalRequest);
          } catch (e) {
            this.clearTokensAndRedirect();
            return Promise.reject(e);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  async register(data: RegisterRequest): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.post(
      API_ENDPOINTS.AUTH.REGISTER,
      data
    );
    return response.data;
  }

  async requestEmailOtp(email: string): Promise<void> {
    await this.api.post("/v1/auth/request-email-otp", { email });
  }

  async verifyEmailOtp(
    email: string,
    otp: string
  ): Promise<{ verified: boolean }> {
    const response: AxiosResponse<{ verified: boolean }> = await this.api.post(
      "/v1/auth/verify-email-otp",
      { email, otp }
    );
    return response.data;
  }

  async login(data: LoginRequest): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.post(
      API_ENDPOINTS.AUTH.LOGIN,
      data
    );
    return response.data;
  }

  async logout(): Promise<void> {
    await this.api.post(API_ENDPOINTS.AUTH.LOGOUT);
  }

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.post(
      API_ENDPOINTS.AUTH.REFRESH,
      {
        refreshToken,
      }
    );
    return response.data;
  }

  public clearTokens() {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("tokenIssuedAt");
    localStorage.removeItem("isAuthenticated");
    delete (this.api.defaults.headers.common as any)["Authorization"];
  }

  public clearAllStorage() {
    // Clear all localStorage
    localStorage.clear();
    // Clear all sessionStorage
    sessionStorage.clear();
    // Clear API headers
    delete (this.api.defaults.headers.common as any)["Authorization"];
  }

  private clearTokensAndRedirect() {
    // Prevent multiple redirects
    if (this.isRedirecting) return;
    this.isRedirecting = true;

    // Clear tokens first (synchronous operation)
    this.clearTokens();

    // Navigate to sign in page immediately - use replace to prevent back navigation
    if (window.location.pathname !== "/sign_in") {
      window.location.replace("/sign_in");
    }
  }

  private clearAllStorageAndRedirect() {
    // Prevent multiple redirects
    if (this.isRedirecting) return;
    this.isRedirecting = true;

    // Clear all storage first (synchronous operation)
    this.clearAllStorage();

    // Navigate to sign in page immediately - use replace to prevent back navigation
    if (window.location.pathname !== "/sign_in") {
      window.location.replace("/sign_in");
    }
  }

  public setTokens(accessToken: string, refreshToken?: string) {
    if (accessToken) {
      localStorage.setItem("accessToken", accessToken);
      // Store token issuance timestamp for expiration tracking
      localStorage.setItem("tokenIssuedAt", Date.now().toString());
      (this.api.defaults.headers.common as any)[
        "Authorization"
      ] = `Bearer ${accessToken}`;
    }
    if (refreshToken) {
      localStorage.setItem("refreshToken", refreshToken);
    }
    localStorage.setItem("isAuthenticated", "true");
  }

  private async getValidAccessToken(): Promise<string | null> {
    const refreshToken = localStorage.getItem("refreshToken");

    // If no refresh token present, cannot refresh
    if (!refreshToken) return null;

    // Debounce concurrent 401s to a single refresh
    if (this.isRefreshing && this.refreshPromise) {
      try {
        const token = await this.refreshPromise;
        return token;
      } catch {
        return null;
      }
    }

    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      const res = await this.refreshToken(refreshToken);
      const newAccess = res.accessToken;
      const newRefresh = res.refreshToken;
      this.setTokens(newAccess, newRefresh);
      this.isRefreshing = false;
      this.refreshPromise = null;
      return newAccess;
    })();

    try {
      const token = await this.refreshPromise;
      return token;
    } catch (e) {
      this.isRefreshing = false;
      this.refreshPromise = null;
      this.clearTokens();
      return null;
    }
  }

  async getProfile(): Promise<any> {
    const response = await this.api.get(API_ENDPOINTS.AUTH.PROFILE);
    return response.data;
  }

  async updateProfile(profileData: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    companyName: string;
    title: string;
  }): Promise<any> {
    const response = await this.api.put(
      API_ENDPOINTS.AUTH.PROFILE,
      profileData
    );
    return response.data;
  }

  async changePassword(passwordData: {
    currentPassword: string;
    newPassword: string;
  }): Promise<{ accessToken: string; refreshToken: string }> {
    const response: AxiosResponse<{
      accessToken: string;
      refreshToken: string;
    }> = await this.api.post("/v1/auth/change-password", passwordData);
    return response.data;
  }

  async forgotPassword(email: string): Promise<void> {
    await this.api.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email });
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await this.api.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
      token,
      newPassword,
    });
  }

  // Chat
  async createChatResponse(
    leadId: string,
    message: string,
    channel?: string
  ): Promise<{
    response: string;
    messageId?: string;
    deliveryStatus?: string;
  }> {
    const picked = localStorage.getItem("leadIdOverride") || leadId;
    const response: AxiosResponse<{
      response: string;
      messageId?: string;
      deliveryStatus?: string;
    }> = await this.api.post("/v1/chats", {
      leadId: picked,
      message,
      channel,
    });
    return response.data;
  }

  async sendMessageWithChannel(
    leadId: string,
    message: string,
    channel: string
  ): Promise<{
    messageId: string;
    deliveryStatus: string;
    externalMessageId?: string;
    response?: string;
  }> {
    console.log("🔍 [DEBUG] sendMessageWithChannel called with:", {
      leadId,
      message: message.substring(0, 50) + "...",
      channel,
    });

    const picked = localStorage.getItem("leadIdOverride") || leadId;
    const response: AxiosResponse<{
      messageId: string;
      deliveryStatus: string;
      externalMessageId?: string;
    }> = await this.api.post("/v1/chats/send", {
      leadId: picked,
      message,
      channel,
    });

    console.log(
      "🔍 [DEBUG] sendMessageWithChannel completed, result:",
      response.data
    );
    return response.data;
  }

  async getMessageDeliveryStatus(messageId: string): Promise<{
    deliveryStatus: string;
    deliveredAt?: string;
    deliveryError?: string;
  }> {
    const response: AxiosResponse<{
      deliveryStatus: string;
      deliveredAt?: string;
      deliveryError?: string;
    }> = await this.api.get(`/v1/chats/status/${messageId}`);
    return response.data;
  }

  // Scheduling
  async scheduleAppointment(data: {
    leadId: string;
    type: string;
    scheduledAt: string;
    duration?: number;
    notes?: string;
    location?: string;
  }): Promise<{
    scheduleId: string;
    success: boolean;
    message: string;
    notificationSent: boolean;
    channelUsed?: string;
  }> {
    const response: AxiosResponse<{
      scheduleId: string;
      success: boolean;
      message: string;
      notificationSent: boolean;
      channelUsed?: string;
    }> = await this.api.post("/v1/scheduling", data);
    return response.data;
  }

  // Calendar connection + availability
  async getCalendarConnectionStatus(): Promise<{ connected: boolean }> {
    const res = await this.api.get(API_ENDPOINTS.CALENDAR.STATUS);
    return res.data;
  }

  async getCalendarAuthorizationUrl(
    redirectUri: string
  ): Promise<{ authorizationUrl: string }> {
    const res = await this.api.get(API_ENDPOINTS.CALENDAR.AUTH_URL, {
      params: { redirectUri },
    });
    return res.data;
  }

  async completeCalendarAuthorization(
    authCode: string,
    redirectUri: string
  ): Promise<{
    message: string;
    connection: { provider: string; email: string };
  }> {
    const res = await this.api.post(API_ENDPOINTS.CALENDAR.COMPLETE_AUTH, {
      authCode,
      redirectUri,
    });
    return res.data;
  }

  async disconnectCalendar(): Promise<{ message: string }> {
    const res = await this.api.delete(API_ENDPOINTS.CALENDAR.DISCONNECT);
    return res.data;
  }

  async getCalendarAvailability(params: {
    startDate: string;
    endDate: string;
    duration?: number;
  }): Promise<any> {
    const res = await this.api.get(API_ENDPOINTS.CALENDAR.AVAILABILITY, {
      params,
    });
    return res.data;
  }

  // Meetings scheduling APIs
  async getMeetingAvailability(params: {
    startDate: string;
    endDate: string;
    duration: number;
  }): Promise<{ slots: { start: string; end: string }[] } | any> {
    const res = await this.api.get(API_ENDPOINTS.MEETINGS.AVAILABILITY, {
      params,
    });
    return res.data;
  }

  async scheduleMeeting(data: {
    title: string;
    description?: string;
    startTime: string;
    endTime: string;
    attendees: string[];
    location?: string;
    timeZone: string;
    leadId: string;
  }): Promise<any> {
    const res = await this.api.post(API_ENDPOINTS.MEETINGS.SCHEDULE, data);
    return res.data;
  }

  async rescheduleMeeting(
    eventId: string,
    newTime: { start: string; end: string },
    timeZone?: string
  ): Promise<any> {
    const res = await this.api.post(
      API_ENDPOINTS.MEETINGS.RESCHEDULE(eventId),
      { newTime, timeZone }
    );
    return res.data;
  }

  async cancelMeeting(eventId: string): Promise<any> {
    const res = await this.api.delete(API_ENDPOINTS.MEETINGS.CANCEL(eventId));
    return res.data;
  }

  async getMeetingDetails(eventId: string): Promise<any> {
    const res = await this.api.get(API_ENDPOINTS.MEETINGS.DETAILS(eventId));
    return res.data;
  }

  async getScheduledAppointments(
    leadId: string
  ): Promise<{ appointments: any[] }> {
    const response: AxiosResponse<{ appointments: any[] }> = await this.api.get(
      `/v1/scheduling/lead/${leadId}`
    );
    return response.data;
  }

  async cancelAppointment(
    scheduleId: string
  ): Promise<{ success: boolean; message: string }> {
    const response: AxiosResponse<{ success: boolean; message: string }> =
      await this.api.delete(`/v1/scheduling/${scheduleId}`);
    return response.data;
  }

  async getSuggestedResponse(leadId: string): Promise<{ suggestion: string }> {
    const picked = localStorage.getItem("leadIdOverride") || leadId;
    const response: AxiosResponse<{ suggestion: string }> = await this.api.post(
      "/v1/chats/suggest",
      { leadId: picked }
    );
    return response.data;
  }

  async getClientReply(
    leadId: string,
    message?: string
  ): Promise<{ response: string }> {
    console.log("🔍 [DEBUG] getClientReply called with:", {
      leadId,
      message: message?.substring(0, 50) + "...",
    });

    const picked = localStorage.getItem("leadIdOverride") || leadId;
    const response: AxiosResponse<{ response: string }> = await this.api.post(
      "/v1/chats/client",
      { leadId: picked, message }
    );

    console.log(
      "🔍 [DEBUG] getClientReply completed, response:",
      response.data.response?.substring(0, 50) + "..."
    );
    return response.data;
  }

  async simpleChat(payload: {
    message: string;
    system?: string;
    temperature?: number;
  }): Promise<{ response: string }> {
    const res: AxiosResponse<{ response: string }> = await this.api.post(
      "/v1/chats/simple",
      payload
    );
    return res.data;
  }

  async resolveLeadId(): Promise<{ leadId: string | null }> {
    const response: AxiosResponse<{ leadId: string | null }> =
      await this.api.get("/v1/chats/resolve-lead");
    return response.data;
  }

  // Autopilot API methods
  async configureAutopilot(
    leadId: string,
    config: {
      answerType: "Manual" | "Suggested" | "Autopilot";
      instantly?: boolean;
      autopilotTime?: string;
      parsedTime?: string;
    }
  ): Promise<{ success: boolean; error?: string }> {
    const response: AxiosResponse<{ success: boolean; error?: string }> =
      await this.api.post("/v1/autopilot/configure", { leadId, ...config });
    return response.data;
  }

  async getAutopilotConfig(leadId: string): Promise<{
    leadId: string;
    realtorId: string;
    answerType: "Manual" | "Suggested" | "Autopilot";
    instantly?: boolean;
    autopilotTime?: string;
    parsedTime?: string;
  } | null> {
    const response: AxiosResponse<{
      leadId: string;
      realtorId: string;
      answerType: "Manual" | "Suggested" | "Autopilot";
      instantly?: boolean;
      autopilotTime?: string;
      parsedTime?: string;
    } | null> = await this.api.get(`/v1/autopilot/config/${leadId}`);
    return response.data;
  }

  async executeAutopilotResponse(leadId: string): Promise<{
    success: boolean;
    messageId?: string;
    deliveryStatus?: string;
    error?: string;
  }> {
    const response: AxiosResponse<{
      success: boolean;
      messageId?: string;
      deliveryStatus?: string;
      error?: string;
    }> = await this.api.post(`/v1/autopilot/execute/${leadId}`);
    return response.data;
  }

  async getAutopilotStatus(): Promise<{
    instantLeads: number;
    scheduledLeads: number;
    totalLeads: number;
  }> {
    const response: AxiosResponse<{
      instantLeads: number;
      scheduledLeads: number;
      totalLeads: number;
    }> = await this.api.get("/v1/autopilot/status");
    return response.data;
  }

  // Lead insights (AI extraction)
  async getLeadInsights(leadId: string): Promise<Record<string, any>> {
    const res = await this.api.get(`/v1/autopilot/insights/${leadId}`);
    return res.data || {};
  }

  async disableAutopilot(
    leadId: string
  ): Promise<{ success: boolean; error?: string }> {
    const response: AxiosResponse<{ success: boolean; error?: string }> =
      await this.api.delete(`/v1/autopilot/disable/${leadId}`);
    return response.data;
  }

  async ensureLeadIdOverride(): Promise<void> {
    const cur = localStorage.getItem("leadIdOverride");
    if (cur && cur.length > 20) return;

    try {
      const { leadId } = await this.resolveLeadId();
      if (leadId) {
        localStorage.setItem("leadIdOverride", leadId);
      }
    } catch (e) {
      console.warn("[ensureLeadIdOverride] could not resolve leadId", e);
    }
  }

  // FUB
  async getLeads(): Promise<Lead[]> {
    const res = await this.api.get("/v1/leads");
    return res.data?.data?.leads ?? [];
  }

  async getLead(leadId: string | number): Promise<Lead | any> {
    const res = await this.api.get(API_ENDPOINTS.FUB.LEAD(String(leadId)));
    return res.data?.data?.lead ?? res.data?.lead ?? res.data ?? null;
  }

  async refreshLeadIntel(
    leadId: string | number
  ): Promise<{ buySell: string; qualityScore: number }> {
    const res = await this.api.post(`/v1/leads/${leadId}/intel/refresh`);
    const data = res.data?.data || {};
    return {
      buySell: data.buySell,
      qualityScore: Number(data.qualityScore || 0),
    };
  }

  async getLeadConversations(leadId: string | number): Promise<any[]> {
    const res = await this.api.get(
      API_ENDPOINTS.FUB.CONVERSATIONS(String(leadId))
    );
    return (
      res.data?.data?.conversations ??
      res.data?.data?.messages ??
      res.data?.conversations ??
      res.data ??
      []
    );
  }

  // Convert a lead
  async convertLead(leadId: string | number): Promise<any> {
    const res = await this.api.post(`/v1/leads/${leadId}/convert`);
    return res.data;
  }

  // Delete all chat messages for a lead (current user)
  async deleteChat(
    leadId: string | number
  ): Promise<{ success: boolean; deleted?: number }> {
    const res = await this.api.delete(`/v1/chats/${leadId}`);
    return res.data;
  }

  // Update a single field on a lead
  async updateLeadField(
    leadId: string | number,
    fieldKey: string,
    value: any
  ): Promise<any> {
    const endpoint = API_ENDPOINTS.FUB.LEAD(String(leadId));
    const payload: Record<string, any> = { [fieldKey]: value };
    console.log("[api.updateLeadField] PATCH", endpoint, payload);
    const res = await this.api.patch(endpoint, payload);
    console.log("[api.updateLeadField] response", res.status, res.data);
    return res.data;
  }

  // Update multiple fields on a lead (single PATCH)
  async updateLead(
    leadId: string | number,
    data: Record<string, any>
  ): Promise<any> {
    const endpoint = API_ENDPOINTS.FUB.LEAD(String(leadId));
    console.log("[api.updateLead] PATCH", endpoint, data);
    const res = await this.api.patch(endpoint, data);
    console.log("[api.updateLead] response", res.status, res.data);
    return res.data;
  }

  async getNextBestAction(leadId: string | number): Promise<string> {
    const res = await this.api.get(
      API_ENDPOINTS.FUB.NEXT_ACTION(String(leadId))
    );
    return (
      res.data?.data?.nextAction ??
      res.data?.nextAction ??
      res.data?.action ??
      ""
    );
  }

  async createLeadNote(leadId: string | number, text: string): Promise<any> {
    const res = await this.api.post(
      API_ENDPOINTS.FUB.CREATE_NOTE(String(leadId)),
      { text }
    );
    return res.data;
  }

  async getInterestedProperties(
    leadId: string | number
  ): Promise<{ leadId: string; interestedProperties: any[] }> {
    const res = await this.api.get(`/v1/leads/${leadId}/interested-properties`);
    return res.data.data;
  }

  async getSyncStatus(): Promise<SyncStatus> {
    return (await this.api.get<SyncStatus>("/v1/leads/sync-status")).data;
  }

  async ingestLeads(): Promise<any> {
    return (await this.api.post("/v1/leads/ingest")).data;
  }

  // Payments / Stripe
  async createCustomerPortalSession(
    returnUrl: string
  ): Promise<{ url: string }> {
    const response: AxiosResponse<{ url: string }> = await this.api.post(
      API_ENDPOINTS.PAYMENTS.CUSTOMER_PORTAL,
      {
        returnUrl,
      }
    );
    return response.data;
  }

  async getSubscriptionStatus(): Promise<{
    status: string;
    isActive: boolean;
    currentPeriodEnd: string | null;
    trialEndDate: string | null;
    cancelAtPeriodEnd: boolean;
    hasStripeCustomer: boolean;
    hasStripeSubscription: boolean;
    hasPaymentMethod: boolean;
    isInTrial: boolean;
    trialDaysRemaining: number;
  }> {
    const response = await this.api.get("/v1/payments/subscription-status");
    return response.data;
  }

  async createSubscription(): Promise<{
    subscriptionId: string;
    status: string;
    message: string;
  }> {
    const response = await this.api.post("/v1/payments/create-subscription");
    return response.data;
  }

  async initiateCall(
    leadId: string,
    message?: string
  ): Promise<{
    success: boolean;
    callId?: string;
    status?: string;
    error?: string;
  }> {
    const response = await this.api.post("/v1/communication/call/initiate", {
      leadId,
      message: message || "Calling lead",
    });
    return response.data;
  }

  async getCallStatus(callId: string): Promise<{
    success: boolean;
    callId: string;
    status?: {
      callId: string;
      status: string;
      duration?: number;
      recordingUrl?: string;
    };
    error?: string;
  }> {
    const response = await this.api.get(
      `/v1/communication/call/status/${callId}`
    );
    return response.data;
  }

  async initiateWebToPhoneCall(
    leadId: string,
    message?: string
  ): Promise<{
    success: boolean;
    callId?: string;
    conferenceSid?: string;
    conferenceName?: string;
    webRtcToken?: string;
    status?: string;
    error?: string;
  }> {
    const response = await this.api.post(
      "/v1/communication/call/web-to-phone",
      {
        leadId,
        message: message || "Calling lead",
      }
    );
    return response.data;
  }

  async endCall(callId: string): Promise<{ success: boolean }> {
    const response = await this.api.post("/v1/communication/call/end", {
      callId,
    });
    return response.data;
  }

  async getVoiceAccessToken(
    conferenceName: string
  ): Promise<{ token: string; conferenceName: string }> {
    const response = await this.api.get("/v1/communication/voice/token", {
      params: { conferenceName },
    });
    return response.data;
  }

  async getConferenceByFriendlyName(
    conferenceName: string
  ): Promise<{ success: boolean; conference: any }> {
    const response = await this.api.get(
      "/v1/communication/conference/by-name",
      { params: { conferenceName } }
    );
    return response.data;
  }

  // Dashboard Analytics API
  async getDashboardOverview() {
    const response = await this.api.get("/v1/dashboard/overview");
    return response.data;
  }

  async getKPIMetrics(year: number, month: number) {
    const response = await this.api.get("/v1/dashboard/kpi", {
      params: { year, month },
    });
    return response.data;
  }

  async getChartData(startDate?: string, endDate?: string) {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await this.api.get("/v1/dashboard/chart", { params });
    return response.data;
  }

  async getDashboardStats() {
    const response = await this.api.get("/v1/dashboard/stats");
    return response.data;
  }

  // FUB Connection Management
  async getFubConnection(): Promise<{
    id: string;
    isConnected: boolean;
    accountId: string | null;
    allowedIPs: string | null;
    connectedAt: Date | null;
    lastSyncAt: Date | null;
  }> {
    const response = await this.api.get("/v1/fub/connection");
    return response.data;
  }

  async connectFub(data: {
    apiKey: string;
    accountId?: string;
    xSystemKey?: string;
    webhookSecret?: string;
    allowedIPs?: string;
  }): Promise<{
    id: string;
    isConnected: boolean;
    accountId: string | null;
    allowedIPs: string | null;
    connectedAt: Date | null;
    lastSyncAt: Date | null;
  }> {
    const response = await this.api.post("/v1/fub/connect", data);
    return response.data;
  }

  async updateFubConnection(data: {
    apiKey?: string;
    accountId?: string;
    xSystemKey?: string;
    webhookSecret?: string;
    allowedIPs?: string;
  }): Promise<{
    id: string;
    isConnected: boolean;
    accountId: string | null;
    allowedIPs: string | null;
    connectedAt: Date | null;
    lastSyncAt: Date | null;
  }> {
    const response = await this.api.put("/v1/fub/connection", data);
    return response.data;
  }

  async disconnectFub(): Promise<void> {
    await this.api.delete("/v1/fub/connection");
  }

  async testFubConnection(data: {
    apiKey: string;
    accountId: string;
    xSystemKey: string;
  }): Promise<{ valid: boolean }> {
    const response = await this.api.post("/v1/fub/test", data);
    return response.data;
  }

  // MLS Listings
  async searchListings(params: {
    minPrice?: number;
    maxPrice?: number;
    minBedrooms?: number;
    maxBedrooms?: number;
    minBathrooms?: number;
    maxBathrooms?: number;
    propertyType?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    status?: string;
    minSquareFootage?: number;
    maxSquareFootage?: number;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<{
    success: boolean;
    data: {
      properties: any[];
      total: number;
      limit: number;
      offset: number;
    };
  }> {
    const response = await this.api.get("/v1/listings/search", { params });
    return response.data;
  }

  async getListingDetails(id: string): Promise<{
    success: boolean;
    data: any;
  }> {
    const response = await this.api.get(`/v1/listings/${id}`);
    return response.data;
  }

  async getSuggestedListings(
    leadId: string,
    limit?: number
  ): Promise<{
    success: boolean;
    data: {
      leadId: string;
      suggestions: any[];
    };
  }> {
    const response = await this.api.get("/v1/listings/suggestions", {
      params: { leadId, limit },
    });
    return response.data;
  }

  async addListingFavorite(
    leadId: string,
    propertyId: string
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await this.api.post("/v1/listings/favorites", {
      leadId,
      propertyId,
    });
    return response.data;
  }

  async getListingFavorites(leadId: string): Promise<{
    success: boolean;
    data: {
      leadId: string;
      favorites: any[];
    };
  }> {
    const response = await this.api.get(`/v1/listings/favorites/${leadId}`);
    return response.data;
  }

  // Account deletion
  async deleteAccount(): Promise<void> {
    await this.api.delete("/v1/account");
    this.clearTokens();
  }

  // Notifications
  async getNotifications(limit = 50): Promise<any[]> {
    try {
      console.log("🔔 [API] Fetching notifications...");
      const response = await this.api.get(`/v1/notifications?limit=${limit}`);
      console.log("📊 [API] Notifications response:", response.data);
      return response.data?.data?.notifications || [];
    } catch (error) {
      console.error("❌ [API] Failed to fetch notifications:", error);
      throw error;
    }
  }

  async getUnreadCount(): Promise<number> {
    try {
      console.log("🔢 [API] Fetching unread count...");
      const response = await this.api.get("/v1/notifications/unread-count");
      console.log("📊 [API] Unread count response:", response.data);
      return response.data?.data?.count || 0;
    } catch (error) {
      console.error("❌ [API] Failed to fetch unread count:", error);
      throw error;
    }
  }

  async markNotificationAsRead(notificationId: string): Promise<void> {
    await this.api.patch(`/v1/notifications/${notificationId}/read`);
  }

  async dismissNotification(notificationId: string): Promise<void> {
    await this.api.delete(`/v1/notifications/${notificationId}`);
  }

  async dismissAllNotifications(): Promise<void> {
    await this.api.delete("/v1/notifications");
  }

  // Token expiration tracking methods
  public getTokenExpiry(): number | null {
    const issuedAt = localStorage.getItem("tokenIssuedAt");
    if (!issuedAt) return null;

    // Token expires after 60 minutes (3600000 ms)
    const TOKEN_LIFETIME_MS = 60 * 60 * 1000;
    return parseInt(issuedAt) + TOKEN_LIFETIME_MS;
  }

  public isTokenExpiringSoon(bufferMinutes: number = 5): boolean {
    const expiry = this.getTokenExpiry();
    if (!expiry) return false;

    const bufferMs = bufferMinutes * 60 * 1000;
    return Date.now() >= expiry - bufferMs;
  }

  public getTimeUntilExpiry(): number {
    const expiry = this.getTokenExpiry();
    if (!expiry) return 0;
    return Math.max(0, expiry - Date.now());
  }
}

export const apiService = new ApiService();
export default apiService;
